import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { asRows } from '@shared/api/envelope';
import { key } from '@shared/api/query-keys';
import { waitForDerivation } from '@shared/assets/derivation';

import { adminItemKey, subjectArtworksKey } from './queries';
import type { ContentItemDetail, ExternalApplyResult, ExternalCandidate } from './types';
import { putToPresignedTarget, readImageGeometry } from './upload';

/**
 * The writes both authoring surfaces perform.
 *
 * **No mutation auto-retries and none is optimistic.** Authoring writes are
 * cheap to repeat by hand and expensive to guess at: a retried create makes a
 * second draft, and a row that renders as saved before the server agreed is a
 * claim the operator cannot check.
 *
 * **Nothing here transitions the publish state.** Publish, schedule, and
 * unpublish live on the content list and nowhere else — one decision, one place.
 */

export interface TitleWriteBody {
  title: string;
  description: string;
  categoryIds: string[];
  tagIds: string[];
  /**
   * The type's own declared fields, spelled the way the registry spells them.
   *
   * snake_case here and camelCase around it is not an inconsistency: everything
   * else on this body is a **field** of the request model, which the service
   * aliases; `metadata` is a JSON column's contents, and its keys are data. An
   * alias cannot reach inside it, and the service validates these names literally.
   */
  metadata: {
    production_year: number | null;
    producing_country: string | null;
    age_rating: string | null;
  };
}

/** What `POST /admin/contentItems` actually answers. */
interface CreatedContentItem {
  contentItemId?: string;
  item?: { id?: string };
  id?: string;
}

/**
 * The new item's identifier, from whichever key the answer carried it under.
 *
 * `contentItemId` is what the service sends today. `item.id` and `id` are read as
 * well so a shape change on the wire degrades into a working console rather than a
 * silent duplicate-draft loop.
 */
export function contentItemIdOf(created: CreatedContentItem): string {
  return created.contentItemId ?? created.item?.id ?? created.id ?? '';
}

function useItemInvalidation() {
  const queryClient = useQueryClient();

  return (contentItemId: string) => {
    void queryClient.invalidateQueries({ queryKey: adminItemKey(contentItemId) });
    void queryClient.invalidateQueries({ queryKey: key('content', 'adminItems') });
  };
}

/** A new title is **always** a draft. There is no state field to send. */
export function useCreateContentItem() {
  const invalidate = useItemInvalidation();

  return useMutation({
    // **The create answer is not a `ContentItemDetail`.** It is
    // `{ contentItemId, item: { … } }`, so `created.id` was `undefined` — and an
    // `undefined` id is not a crash, it is a silent stall: `persist()` returned null,
    // `onSave` bailed on `if (!id) return`, no confirmation was ever rendered, the
    // draft id was never held, and every further press of «احفظ العنوان» POSTed a
    // *second* draft. The provider lookup, which runs through the same `persist()`,
    // never had an id to look up against and so never sent its request at all.
    mutationFn: ({ contentType, body }: { contentType: string; body: TitleWriteBody }) =>
      api.post<CreatedContentItem>('content', '/admin/contentItems', { contentType, ...body }),
    onSuccess: (created) => invalidate(contentItemIdOf(created)),
    retry: false,
  });
}

/** `PATCH` semantics: the service applies only what was sent. */
export function useUpdateContentItem() {
  const invalidate = useItemInvalidation();

  return useMutation({
    mutationFn: ({ contentItemId, body }: { contentItemId: string; body: Partial<TitleWriteBody> }) =>
      api.patch<ContentItemDetail>('content', `/admin/contentItems/${contentItemId}`, body),
    onSuccess: (_data, variables) => invalidate(variables.contentItemId),
    retry: false,
  });
}

/**
 * The provider lookup. **Read-only — it never writes**, which is what lets the
 * operator run it as often as they like while they decide between candidates.
 *
 * Enrichment is an accelerator, never a dependency: when the provider is
 * unreachable the panel says so and the form stays fully usable by hand.
 */
export function useLookupExternalMetadata() {
  return useMutation({
    // **The service answers `{ candidates }`, never a bare array.** Typing the
    // response as the array itself made `matches.length` `undefined`, so a
    // search that matched read exactly like a search that had not run: no
    // candidates drawn, and no «لا نتائج» either.
    mutationFn: async ({ contentItemId, query }: { contentItemId: string; query: string }) => {
      const answer = await api.post<{ candidates: ExternalCandidate[] }>(
        'content',
        `/admin/contentItems/${contentItemId}/externalMetadata:lookup`,
        { query },
      );
      return answer.candidates ?? [];
    },
    retry: false,
  });
}

/**
 * The one-action fill.
 *
 * **The values land in the real inputs**, not in a parallel copy beside them —
 * which is what keeps every filled field editable afterwards. The response says
 * what it applied, what it skipped, and what conflicted, so the surface can
 * report rather than claim.
 */
export function useApplyExternalMetadata() {
  const invalidate = useItemInvalidation();

  return useMutation({
    mutationFn: ({ contentItemId, externalId }: { contentItemId: string; externalId: string }) =>
      api.post<ExternalApplyResult>(
        'content',
        `/admin/contentItems/${contentItemId}/externalMetadata:apply`,
        { externalId },
      ),
    onSuccess: (_data, variables) => invalidate(variables.contentItemId),
    retry: false,
  });
}

/**
 * Re-fetch the scores on a title that already exists, published or not.
 *
 * It **touches ratings and nothing else** — an operator-edited synopsis is not
 * overwritten by a later fetch, which is why the invalidation set is the item
 * alone and not the list.
 */
export function useRefetchExternalRatings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contentItemId: string) =>
      api.post<ContentItemDetail>(
        'content',
        `/admin/contentItems/${contentItemId}/externalMetadata:refetchRatings`,
      ),
    onSuccess: (_data, contentItemId) =>
      void queryClient.invalidateQueries({ queryKey: adminItemKey(contentItemId) }),
    retry: false,
  });
}

interface ArtworkRegistration {
  artworkId: string;
  uploadTarget: string;
}

/**
 * The cover, in the three steps the contract has.
 *
 * The service registers the artwork and hands back a presigned target; the
 * browser PUTs the bytes straight to storage; `:completeUpload` is the commit
 * point, and the only place the derivation is queued — the service reads the
 * stored object itself rather than trusting anything declared on this side.
 * **One upload produces four purposes** — the thumbnail is the required one,
 * and hero, slider, and background derive from it by proportional crop.
 *
 * **The dimensions are read, not declared.** They are what the per-ratio minimum
 * is checked against, and the service re-reads them out of the object at the
 * commit; a guess would surface as a refusal after the whole file had already
 * been transferred.
 *
 * This is the call that makes a title publishable: `cover` is one of `content`'s
 * required fields and the derived thumbnail's URL is what fills it.
 */
export function useUploadCover() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contentItemId, file }: { contentItemId: string; file: File }) => {
      const geometry = await readImageGeometry(file);

      // **Read BEFORE the upload, because «present» is not «new».** A title that
      // already carries a cover answers non-null on the first poll — the OLD
      // url — so a wait that only asked for presence returned instantly, the
      // invalidation refetched the column the crop worker had not written yet,
      // and REPLACING a cover put the previous image back on screen. Every
      // derivation gets its own ULID-suffixed key, so a changed url is the one
      // signal that this upload is the one being drawn.
      const previousCoverUrl = await readCoverUrl(contentItemId);

      const registration = await api.post<ArtworkRegistration>('assets', '/artworks', {
        subjectType: 'CONTENT_ITEM',
        subjectId: contentItemId,
        declaredContentType: file.type,
        declaredSizeBytes: file.size,
        widthPx: geometry.widthPx,
        heightPx: geometry.heightPx,
      });

      await putToPresignedTarget(registration.uploadTarget, file);
      await api.post<null>('assets', `/artworks/${registration.artworkId}:completeUpload`);

      // **Waited out, not merely invalidated.** The cover this page draws is
      // `content`'s own `cover_artwork_url`, and `assets` fills it from the crop
      // worker once the variants are stored — several seconds after
      // `:completeUpload` answers. Invalidating here and stopping refetched the
      // title while the column was still `null`, so the operator watched the
      // upload succeed and the box stay empty until they reloaded the page by
      // hand. Polling the column itself is the only read that answers the
      // question the operator is actually asking: is my image on the title yet.
      const coverUrl = await waitForDerivation(
        () => readCoverUrl(contentItemId),
        (url) => url !== null && url !== previousCoverUrl,
      );

      return { ...registration, derived: coverUrl !== null && coverUrl !== previousCoverUrl };
    },
    onSuccess: (_data, variables) =>
      void queryClient.invalidateQueries({ queryKey: adminItemKey(variables.contentItemId) }),
    retry: false,
  });
}

/** The one field the cover control draws, read straight off the owning service. */
async function readCoverUrl(contentItemId: string): Promise<string | null> {
  const payload = await api.get<{ item?: { cover_artwork_url?: string | null } }>(
    'content',
    `/admin/contentItems/${contentItemId}`,
  );

  return payload.item?.cover_artwork_url ?? null;
}

/**
 * Artwork for a SEASON or an EPISODE, in the same three steps the cover takes.
 *
 * The route is one route — `POST /artworks` is keyed on `(subjectType,
 * subjectId)` and `ARTWORK_PURPOSES` decides what gets derived from the image —
 * so this is `useUploadCover` with the subject read off the caller instead of
 * pinned to `CONTENT_ITEM`. It stays a second hook rather than a parameter on
 * the first: the cover's success has to invalidate the TITLE, because a derived
 * thumbnail is what makes a title publishable, and a season's has to invalidate
 * that season's own artwork and nothing else.
 *
 * **The ratios are not the title's.** `GROUP: thumbnail` is `2:3` at a floor of
 * 1000×1500 and `MEDIA_ASSET: thumbnail` is `16:9` at 1920×1080 — an episode
 * still, not a poster. The floor is enforced by the service, which answers
 * `422 ASSETS_ARTWORK_TOO_SMALL`; the control states it beforehand so the
 * refusal does not arrive after the whole file was transferred.
 */
export function useUploadSubjectArtwork() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      subjectType,
      subjectId,
      file,
    }: {
      subjectType: string;
      subjectId: string;
      file: File;
    }) => {
      const geometry = await readImageGeometry(file);

      // The same baseline the cover takes: a subject that already holds an image
      // answers the first poll with the OLD row, and «replace the image» drew
      // the image it replaced.
      const previousUrl = await readSubjectArtworkUrl(subjectType, subjectId);

      const registration = await api.post<ArtworkRegistration>('assets', '/artworks', {
        subjectType,
        subjectId,
        declaredContentType: file.type,
        declaredSizeBytes: file.size,
        widthPx: geometry.widthPx,
        heightPx: geometry.heightPx,
      });

      await putToPresignedTarget(registration.uploadTarget, file);
      await api.post<null>('assets', `/artworks/${registration.artworkId}:completeUpload`);

      // Same wait the cover takes, against this subject's own rows: `GET
      // /artworks` answers with `READY` derived rows only, so the read that
      // follows the commit immediately comes back empty and the control drew an
      // empty box under a successful upload.
      const url = await waitForDerivation(
        () => readSubjectArtworkUrl(subjectType, subjectId),
        (found) => found !== null && found !== previousUrl,
      );

      return { ...registration, derived: url !== null && url !== previousUrl };
    },
    onSuccess: (_data, variables) =>
      void queryClient.invalidateQueries({
        queryKey: subjectArtworksKey(variables.subjectType, variables.subjectId),
      }),
    retry: false,
  });
}

/**
 * The subject's one derived image, unwrapped through the envelope the query uses.
 *
 * The url and not the row count: a replacement leaves the count where it was and
 * changes the key, so counting rows cannot tell a new image from the old one.
 */
async function readSubjectArtworkUrl(subjectType: string, subjectId: string): Promise<string | null> {
  const rows = asRows<{ url?: string | null }>(
    await api.get(
      'assets',
      `/artworks?subjectType=${subjectType}&subjectId=${encodeURIComponent(subjectId)}`,
    ),
  );

  return rows[0]?.url ?? null;
}
